





















import { CLASSIFIED_SEARCH_NS } from '~/store/modules/shared/classifieds/search/state'
import { SET_PARAM } from '~/store/modules/shared/classifieds/search/mutation-types'
import { defineComponent, computed } from '~/utils/nuxt3-migration'
import { ciTimes } from '~/icons/source/solid/times'
import { useNamespacedStore } from '~/compositions/store'
import { Facet, Searchable } from '~/models/search/types'
import { treeFromJsObject } from '~/models/shared/tree'
import {
  MAKE_MODELS_REGISTRATION_SEARCH_PARAM,
  MAKE_SEARCH_PARAM,
  MAKES_IDS_SEARCH_PARAM,
  MAKES_SEARCH_PARAM,
  MODEL_SEARCH_PARAM,
  MODELS_IDS_SEARCH_PARAM,
  MODELS_SEARCH_PARAM,
  SERIES_SEARCH_PARAM,
  VARIANT_SEARCH_PARAM
} from '~/constants/makemodels'
import {
  COUNTRY_CODE_SEARCH_PARAM,
  DISTANCE_SEARCH_PARAM,
  POSTCODE_SEARCH_PARAM
} from '~/constants/postcode'
import {
  DURATION_SEARCH_PARAM,
  LEASE_DOWNPAYMENT_FROM_SEARCH_PARAM,
  LEASE_DOWNPAYMENT_TO_SEARCH_PARAM,
  LEASE_FROM_SEARCH_PARAM,
  LEASE_TO_SEARCH_PARAM,
  WITH_VAT_SEARCH_PARAM,
  YEARLY_MILEAGE_SEARCH_PARAM
} from '~/constants/leasing'
import {
  emptyInstallmentsParams,
  emptyLeasingParams
} from '~/constants/search/facets'
import { GeocodingSearchLocation } from '~/models/location/geocoding'
import { createSearchGeolocationString } from '~/utils/geolocation'

export default defineComponent({
  setup(_props, { emit }) {
    const {
      state: classifiedSearchState,
      getters: classifiedSearchGetters,
      dispatch: classifiedSearchDispatch,
      commit: classifiedSearchCommit
    } = useNamespacedStore(CLASSIFIED_SEARCH_NS)

    const loadingFacets = computed(() => classifiedSearchState.loading.facets)

    const params = computed(() => classifiedSearchState.params)

    const selectedFacets = computed(() =>
      classifiedSearchGetters('getSelectedFacets')
    )

    function getUnselectableFacetParams(facet: Facet) {
      return facet.values.humanParams.filter(value => value.isUnselectable)
    }

    function handleSelectedHandlerClick(
      handler: Facet,
      paramValue: string | number,
      paramUrlArg: string
    ) {
      removeUrlArgPairs(handler)

      const facet = classifiedSearchGetters('getFacetByName')(handler.name)

      if (!facet) {
        classifiedSearchDispatch('search')
        return
      }

      const urlArg = paramUrlArg || handler.urlArgNames[0]

      switch (handler.type) {
        case 'KeyValueSearchHandler':
        case 'BlacklistReasonSearchHandler':
        case 'TermSearchHandler':
        case 'StatesSearchHandler':
        case 'ConditionSearchHandler':
        case 'SellerTypeSearchHandler':
        case 'VehicleKteoSearchHandler':
        case 'MediaTypesSearchHandler':
        case 'VehicleFeaturesSearchHandler':
        case 'CrashedSearchHandler':
        case 'BooleanSearchHandler':
        case 'JobExtrasSearchHandler':
        case 'DatetimeSearchHandler':
        case 'ModifiedSearchHandler':
        case 'DeletedDateSearchHandler':
        case 'DiscountConditionSearchHandler':
        case 'DiscountSellerTypeSearchHandler':
        case 'UserTypeSearchHandler':
        case 'JobTypeSearchHandler':
        case 'AnimalOfferTypeSearchHandler':
        case 'OfferTypeSearchHandler':
        case 'FuelTypeSearchHandler': {
          if (handler.multicheck) {
            changeFacet([
              {
                name: urlArg,
                value: handler.values.selected.filter(
                  (s: any) => s !== paramValue
                )
              }
            ])
          } else {
            changeFacet([{ name: urlArg, value: null }])
          }

          break
        }
        case 'BatteryRangeSearchHandler':
        case 'BatteryChargeTimeSearchHandler':
        case 'BatteryQuickChargeTimeSearchHandler':
        case 'BatteryCapacitySearchHandler':
        case 'EngineSizeSearchHandler':
        case 'EnginePowerSearchHandler':
        case 'LengthSearchHandler':
        case 'WeightSearchHandler':
        case 'VehicleRegistrationSearchHandler':
        case 'FloorSearchHandler':
        case 'PriceSearchHandler':
        case 'KilowattSearchHandler':
        case 'ElectricEnginePowerSearchHandler':
        case 'RangeSearchHandler':
        case 'TireWidthSearchHandler':
        case 'TireDiameterSearchHandler':
        case 'TireRatioSearchHandler': {
          const { name } = handler

          if (paramValue.startsWith('>') || name.includes('-from')) {
            changeFacet([{ name: `${name}-from`, value: null }])
          } else if (paramValue.startsWith('<') || name.includes('-to')) {
            changeFacet([{ name: `${name}-to`, value: null }])
          } else {
            // remove the entire value cause from and to are the same
            changeFacet([
              { name: `${name}-from`, value: null },
              { name: `${name}-to`, value: null },
              { name, value: null }
            ])
          }

          break
        }
        case 'CategoryTreeSearchHandler': {
          const categoryTree = treeFromJsObject(
            handler.searchables || [],
            (searchable: Searchable) => searchable.v.toString(),
            (searchable: Searchable) => searchable.k || []
          )

          // Get all descendants of the category being deselected
          const descendants = categoryTree.getDescendants(paramValue.toString())
          const descendantIds = descendants.map(node => Number(node.id))

          // Remove both the category and all its descendants
          const idsToRemove = [paramValue, ...descendantIds]
          const newValue = handler.values.selected.filter(
            (s: number) => !idsToRemove.includes(s)
          )

          changeFacet([
            {
              name: urlArg,
              value: newValue
            }
          ])
          break
        }
        case 'Location2SearchHandler':
        case 'DiscountLocation2SearchHandler':
        case 'LocationSearchHandler':
        case 'DiscountCategorySearchHandler':
        case 'DeletedCategorySearchHandler':
        case 'AdminCategorySearchHandler': {
          if (
            !handler.values.selected ||
            handler.values.selected?.length === 0 ||
            !paramValue
          ) {
            changeFacet([{ name: urlArg, value: null }])
          } else {
            changeFacet([
              {
                name: urlArg,
                value: handler.values.selected.filter(
                  (s: any) => s !== paramValue
                )
              }
            ])
          }

          break
        }
        case 'ShopKeyValueSearchHandler':
        case 'DiscountShopKeyValueSearchHandler': {
          changeFacet([
            {
              name: urlArg,
              value: handler.values.selected.values.filter(
                (v: number) => v !== paramValue
              )
            }
          ])

          break
        }
        case 'ExteriorColorSearchHandler':
        case 'ExteriorColorTypeSearchHandler':
        case 'InteriorColorSearchHandler':
        case 'ClothesColorFieldSearchHandler':
        case 'ShoesColorFieldSearchHandler':
        case 'ClothesSizeFieldSearchHandler':
        case 'ShoesSizeFieldSearchHandler':
        case 'LeasingExtrasSearchHandler':
        case 'SearchableKeyValueSearchHandler': {
          // these facets are of type multiselect and can all be deselected the same way
          changeFacet([
            {
              name: urlArg,
              value: handler.values.selected.filter(
                (s: any) => s !== paramValue
              )
            }
          ])

          break
        }
        case 'FinanceOptionsSearchHandler': {
          changeFacet([
            {
              name: 'installment-from',
              value: null
            },
            {
              name: 'installment-to',
              value: null
            },
            {
              name: 'installments',
              value: null
            },
            {
              name: 'downpayment',
              value: null
            }
          ])

          break
        }
        case 'PromotedSearchHandler': {
          if (urlArg === 'promotion') {
            changeFacet([{ name: 'promotion', value: null }])
          } else if (urlArg === 'has-previous-promotions') {
            changeFacet([{ name: 'has-previous-promotions', value: null }])
          } else if (urlArg === 'promotion-tier') {
            changeFacet([
              {
                name: urlArg,
                value: handler.values.selected.tiers.filter(
                  (s: any) => s !== paramValue
                )
              }
            ])
          } else {
            changeFacet([
              { name: 'promotion-tier', value: null },
              { name: 'has-previous-promotions', value: null },
              { name: 'prootion', value: null }
            ])
          }

          break
        }
        case 'ForMakeModelSearchHandler': {
          const {
            makes: typedMakes,
            models: typedModels
          } = handler.values.typed!
          const {
            makes: untypedMakes,
            models: untypedModels
          } = handler.values.untyped!

          if (urlArg === MAKES_IDS_SEARCH_PARAM) {
            changeFacet([
              {
                name: MAKES_IDS_SEARCH_PARAM,
                value: typedMakes.filter(m => m !== paramValue)
              },
              {
                name: MODELS_IDS_SEARCH_PARAM,
                value: []
              }
            ])
          } else if (urlArg === MAKES_SEARCH_PARAM) {
            changeFacet([
              {
                name: MAKES_SEARCH_PARAM,
                value: untypedMakes.filter(m => m !== paramValue)
              },
              {
                name: MODELS_SEARCH_PARAM,
                value: []
              }
            ])
          } else if (urlArg === MODELS_IDS_SEARCH_PARAM) {
            changeFacet([
              {
                name: MODELS_IDS_SEARCH_PARAM,
                value: typedModels.filter(m => m !== paramValue)
              }
            ])
          } else if (urlArg === MODELS_SEARCH_PARAM) {
            changeFacet([
              {
                name: MODELS_SEARCH_PARAM,
                value: untypedModels.filter(m => m !== paramValue)
              }
            ])
          } else if (urlArg === MAKE_MODELS_REGISTRATION_SEARCH_PARAM) {
            changeFacet([
              { name: MAKE_MODELS_REGISTRATION_SEARCH_PARAM, value: null }
            ])
          } else if (urlArg === 'makes_ids' || urlArg === 'models_ids') {
            changeFacet([
              { name: 'makes', value: null },
              { name: 'models', value: null },
              { name: 'makes_ids', value: null },
              { name: 'models_ids', value: null }
            ])
          }

          break
        }
        case 'FreeTextMakeModelSearchHandler': {
          const { makes, models } = handler.values.selected
          const { urlArgNames } = handler

          if (makes.includes(paramValue)) {
            changeFacet([
              {
                name: urlArgNames[1],
                value: makes.filter((m: any) => m !== paramValue)
              }
            ])
          } else if (models.includes(paramValue)) {
            changeFacet([
              {
                name: urlArgNames[0],
                value: models.filter((m: any) => m !== paramValue)
              }
            ])
          } else {
            changeFacet([{ name: urlArg, value: null }])
          }

          break
        }
        case 'MakeModelSearchHandler': {
          const { selected }: any = handler.values
          const { makes, models } = selected

          if (urlArg === MAKE_SEARCH_PARAM) {
            changeFacet([
              {
                name: MAKE_SEARCH_PARAM,
                value: makes.filter((m: any) => m !== paramValue)
              }
            ])
          } else if (
            urlArg === MODEL_SEARCH_PARAM ||
            urlArg === SERIES_SEARCH_PARAM
          ) {
            changeFacet([
              {
                name: MODEL_SEARCH_PARAM,
                value: models.filter((m: any) => m !== paramValue)
              }
            ])
          } else if (urlArg === VARIANT_SEARCH_PARAM) {
            changeFacet([{ name: VARIANT_SEARCH_PARAM, value: null }])
          }

          break
        }
        case 'PostcodeDistanceSearchHandler': {
          changeFacet([
            { name: POSTCODE_SEARCH_PARAM, value: null },
            { name: COUNTRY_CODE_SEARCH_PARAM, value: null },
            { name: DISTANCE_SEARCH_PARAM, value: null }
          ])

          break
        }
        case 'LeasingOptionsSearchHandler': {
          if (urlArg === 'lease') {
            changeFacet([
              { name: LEASE_FROM_SEARCH_PARAM, value: null },
              { name: LEASE_TO_SEARCH_PARAM, value: null }
            ])
          } else if (urlArg === 'lease-downpayment') {
            changeFacet([
              { name: LEASE_DOWNPAYMENT_FROM_SEARCH_PARAM, value: null },
              { name: LEASE_DOWNPAYMENT_TO_SEARCH_PARAM, value: null }
            ])
          } else if (urlArg === 'with-vat') {
            changeFacet([{ name: WITH_VAT_SEARCH_PARAM, value: null }])
          } else if (urlArg === 'duration') {
            changeFacet([{ name: DURATION_SEARCH_PARAM, value: null }])
          } else if (urlArg === 'yearly-mileage') {
            changeFacet([{ name: YEARLY_MILEAGE_SEARCH_PARAM, value: null }])
          }
          break
        }
        case 'VisibilitySearchHandler': {
          changeFacet([{ name: 'dealersonly', value: false }])

          break
        }
        case 'FinanceSearchHandler': {
          if (paramValue === 'leasing') {
            changeFacet([
              ...emptyLeasingParams,
              ...[
                { name: 'leasing-extra', value: null },
                { name: 'finance', value: null }
              ]
            ])
          } else if (paramValue === 'with_instalments') {
            changeFacet([
              { name: 'sort', value: null },
              { name: 'price', value: null },
              { name: 'price-from', value: null },
              { name: 'price-to', value: null },
              { name: 'withprice', value: null },
              { name: 'discount', value: null },
              { name: 'wholesale', value: null },
              { name: 'dealersonly', value: null },
              { name: 'finance', value: null },
              { name: 'cheap', value: null },
              ...emptyInstallmentsParams,
              ...emptyLeasingParams
            ])
          } else {
            changeFacet([{ name: urlArg, value: null }])
          }

          break
        }
        case 'RentalPickupLocationSearchHandler': {
          changeFacet([
            {
              name: 'rich-geolocation',
              value: handler.values.selected.geolocations
                .filter((g: GeocodingSearchLocation) => g.name !== paramValue)
                .map(createSearchGeolocationString)
            }
          ])

          break
        }
        default: {
          changeFacet([{ name: urlArg, value: null }])
        }
      }
    }

    function changeFacet(params: { name: string; value: any }[]) {
      classifiedSearchDispatch('facetChanged', { params })
    }

    function removeUrlArgPairs(handler: Facet) {
      emit('remove-pairs', handler)
      const { humanParams } = handler.values
      humanParams.forEach(humanParam => {
        const { selectedUrlArgValuePairs } = humanParam
        if (selectedUrlArgValuePairs) {
          selectedUrlArgValuePairs.forEach(valuePair => {
            const { value, urlarg } = valuePair
            Object.entries(params.value).forEach(([paramName, paramValue]) => {
              if (paramName === urlarg && paramValue.includes(value)) {
                classifiedSearchCommit(SET_PARAM, {
                  name: paramName,
                  value: null
                })
              }
            })
          })
        }
      })
    }

    return {
      ciTimes,
      loadingFacets,
      selectedFacets,
      getUnselectableFacetParams,
      handleSelectedHandlerClick
    }
  }
})
